/**
=========================================================
* Material Dashboard 3 PRO React - v2.4.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2024 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import swal from "sweetalert";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormControl from "@mui/material/FormControl";
import FormLabel from "@mui/material/FormLabel";
import TextField from "@mui/material/TextField";
import Divider from "@mui/material/Divider";

// Material Dashboard 3 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";

// Material Dashboard 3 PRO React examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

import { API_ESA_OFFICE } from "utils";

function FormSurvey() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [survey, setSurvey] = useState(null);
  const [pertanyaan, setPertanyaan] = useState([]);
  const [formData, setFormData] = useState({
    responden: {
      nama: "",
      pendidikan: "",
      pekerjaan: "",
      id_smart: null,
    },
    jawaban: {},
  });

  useEffect(() => {
    if (id) {
      fetchSurveyDetail();
    }
  }, [id]);

  const fetchSurveyDetail = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API_ESA_OFFICE}survey-kepuasan/survey/${id}/detail`
      );
      setSurvey(response.data.survey);
      setPertanyaan(response.data.pertanyaan || []);

      // Initialize jawaban object
      const initialJawaban = {};
      response.data.pertanyaan.forEach((p) => {
        if (p.tipe === "nilai") {
          initialJawaban[p.id] = null;
        } else if (p.tipe === "teks") {
          initialJawaban[p.id] = "";
        }
      });
      setFormData((prev) => ({
        ...prev,
        jawaban: initialJawaban,
      }));
    } catch (error) {
      console.error("Error fetching survey detail:", error);
      swal({
        title: "Error",
        text: "Gagal memuat data survey. Survey tidak ditemukan atau telah dihapus.",
        icon: "error",
        button: "OK",
      }).then(() => {
        navigate("/");
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRespondenChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      responden: {
        ...prev.responden,
        [field]: value,
      },
    }));
  };

  const handleJawabanChange = (pertanyaanId, value) => {
    setFormData((prev) => ({
      ...prev,
      jawaban: {
        ...prev.jawaban,
        [pertanyaanId]: value,
      },
    }));
  };

  const validateForm = () => {
    // Validasi data responden (opsional, bisa kosong)
    // Validasi jawaban
    for (const p of pertanyaan) {
      if (p.tipe === "nilai" && !formData.jawaban[p.id]) {
        swal({
          title: "Perhatian",
          text: `Pertanyaan "${p.pertanyaan}" harus diisi`,
          icon: "warning",
          button: "OK",
        });
        return false;
      }
      if (p.tipe === "teks" && !formData.jawaban[p.id]?.trim()) {
        swal({
          title: "Perhatian",
          text: `Pertanyaan "${p.pertanyaan}" harus diisi`,
          icon: "warning",
          button: "OK",
        });
        return false;
      }
    }
    return true;
  };

  const handleSubmit = async () => {
    if (submitting) return;

    if (!validateForm()) {
      return;
    }

    setSubmitting(true);
    try {
      // Format jawaban untuk backend
      const jawabanArray = pertanyaan.map((p) => {
        const jawaban = {
          id_pertanyaan: p.id,
        };

        if (p.tipe === "nilai") {
          // Untuk tipe nilai, formData.jawaban[p.id] berisi id_opsi
          jawaban.id_opsi = formData.jawaban[p.id];
        } else if (p.tipe === "teks") {
          // Untuk tipe teks, formData.jawaban[p.id] berisi teks
          jawaban.jawaban_teks = formData.jawaban[p.id];
        }

        return jawaban;
      });

      const payload = {
        responden: formData.responden,
        jawaban: jawabanArray,
      };

      await axios.post(
        `${API_ESA_OFFICE}survey-kepuasan/survey/${id}/submit`,
        payload
      );

      swal({
        title: "Berhasil!",
        text: "Terima kasih telah mengisi survey kepuasan. Jawaban Anda telah tersimpan.",
        icon: "success",
        button: "OK",
      }).then(() => {
        // Reset form atau redirect
        setFormData({
          responden: {
            nama: "",
            pendidikan: "",
            pekerjaan: "",
            id_smart: null,
          },
          jawaban: {},
        });
      });
    } catch (error) {
      console.error("Error submitting survey:", error);
      swal({
        title: "Error",
        text:
          error.response?.data?.message ||
          "Gagal mengirim jawaban. Silakan coba lagi.",
        icon: "error",
        button: "OK",
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Group pertanyaan by kategori
  const groupPertanyaanByKategori = () => {
    const grouped = {};
    pertanyaan.forEach((p) => {
      const kategori = p.kategori || "Lainnya";
      if (!grouped[kategori]) {
        grouped[kategori] = [];
      }
      grouped[kategori].push(p);
    });
    return grouped;
  };

  if (loading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="100vh"
        >
          <CircularProgress />
          <MDTypography variant="body2" color="text" ml={2}>
            Memuat data survey...
          </MDTypography>
        </MDBox>
        <Footer />
      </DashboardLayout>
    );
  }

  if (!survey) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox pt={6} pb={3}>
          <Card>
            <MDBox p={3}>
              <MDTypography variant="h6" color="error">
                Survey tidak ditemukan
              </MDTypography>
            </MDBox>
          </Card>
        </MDBox>
        <Footer />
      </DashboardLayout>
    );
  }

  const pertanyaanGrouped = groupPertanyaanByKategori();

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6} justifyContent="center">
          <Grid item xs={12} md={10}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
              >
                <MDTypography variant="h4" color="white" textAlign="center">
                  {survey.nama_survey}
                </MDTypography>
                {survey.keterangan && (
                  <MDTypography
                    variant="body2"
                    color="white"
                    textAlign="center"
                    mt={1}
                  >
                    {survey.keterangan}
                  </MDTypography>
                )}
                {survey.nama_layanan && (
                  <MDTypography
                    variant="body2"
                    color="white"
                    textAlign="center"
                    mt={1}
                  >
                    Layanan: {survey.nama_layanan}
                  </MDTypography>
                )}
              </MDBox>

              <MDBox pt={4} pb={3} px={3}>
                {/* Data Responden */}
                <MDBox mb={4}>
                  <MDTypography variant="h6" mb={2}>
                    Data Responden (Opsional)
                  </MDTypography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <MDInput
                        label="Nama"
                        value={formData.responden.nama}
                        onChange={(e) =>
                          handleRespondenChange("nama", e.target.value)
                        }
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <MDInput
                        label="Pendidikan"
                        value={formData.responden.pendidikan}
                        onChange={(e) =>
                          handleRespondenChange("pendidikan", e.target.value)
                        }
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <MDInput
                        label="Pekerjaan"
                        value={formData.responden.pekerjaan}
                        onChange={(e) =>
                          handleRespondenChange("pekerjaan", e.target.value)
                        }
                        fullWidth
                      />
                    </Grid>
                  </Grid>
                </MDBox>

                <Divider sx={{ my: 4 }} />

                {/* Pertanyaan Survey */}
                <MDBox mb={4}>
                  <MDTypography variant="h6" mb={3}>
                    Pertanyaan Survey
                  </MDTypography>

                  {Object.keys(pertanyaanGrouped).map(
                    (kategori, kategoriIndex) => (
                      <MDBox key={kategori} mb={4}>
                        <MDTypography
                          variant="h6"
                          fontWeight="bold"
                          mb={2}
                          color="info"
                        >
                          {kategori}
                        </MDTypography>

                        {pertanyaanGrouped[kategori].map((p, index) => (
                          <MDBox key={p.id} mb={3}>
                            <MDTypography
                              variant="subtitle1"
                              fontWeight="medium"
                              mb={2}
                            >
                              {index + 1}. {p.pertanyaan}
                            </MDTypography>

                            {p.tipe === "nilai" ? (
                              <FormControl component="fieldset">
                                <RadioGroup
                                  value={formData.jawaban[p.id] || ""}
                                  onChange={(e) =>
                                    handleJawabanChange(
                                      p.id,
                                      parseInt(e.target.value)
                                    )
                                  }
                                >
                                  {p.opsi && p.opsi.length > 0 ? (
                                    p.opsi.map((opsi) => (
                                      <FormControlLabel
                                        key={opsi.id}
                                        value={opsi.id.toString()}
                                        control={<Radio />}
                                        label={opsi.label}
                                      />
                                    ))
                                  ) : (
                                    <MDTypography variant="body2" color="error">
                                      Opsi jawaban belum tersedia
                                    </MDTypography>
                                  )}
                                </RadioGroup>
                              </FormControl>
                            ) : (
                              <TextField
                                fullWidth
                                multiline
                                rows={4}
                                value={formData.jawaban[p.id] || ""}
                                onChange={(e) =>
                                  handleJawabanChange(p.id, e.target.value)
                                }
                                placeholder="Masukkan jawaban Anda..."
                                variant="outlined"
                              />
                            )}
                          </MDBox>
                        ))}
                      </MDBox>
                    )
                  )}
                </MDBox>

                {/* Submit Button */}
                <MDBox display="flex" justifyContent="center" mt={4}>
                  <MDButton
                    variant="gradient"
                    color="info"
                    size="large"
                    onClick={handleSubmit}
                    disabled={submitting}
                  >
                    {submitting ? (
                      <>
                        <CircularProgress
                          size={20}
                          color="inherit"
                          sx={{ mr: 1 }}
                        />
                        Mengirim...
                      </>
                    ) : (
                      "Kirim Survey"
                    )}
                  </MDButton>
                </MDBox>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default FormSurvey;
